import type { PropsWithChildren } from 'react';
import { Navigate, useLocation, type To } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useCurrentUser } from '@/features/auth/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { tokenStorage } from '@/services/storage';
import { ROUTES } from '@/config/routes';
import { resolvePostLoginRoute } from '@/config/permissions';
import { AppLoadingState } from '@/components/feedback/AppLoadingState';

/**
 * RequireAuth - Section 6.1's authentication guard. Wraps every route
 * under /account/*, /partner/*, /driver/*, /corporate/*, /admin/*.
 *
 * While `useCurrentUser` is still resolving (e.g. right after a hard
 * refresh, before we know yet whether the stored refresh token is still
 * valid), we show a loading state rather than bouncing straight to
 * /login - that would otherwise flash the login page for a split second
 * on every refresh even for an already-logged-in user.
 */
export function RequireAuth({ children }: PropsWithChildren) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser);
  const hasAnyToken = Boolean(tokenStorage.getAccessToken() || tokenStorage.getRefreshToken());
  const { isLoading } = useCurrentUser();

  if (!hasAnyToken) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }
  if (isLoading && !isAuthenticated) {
    return <AppLoadingState />;
  }
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.login} state={{ from: location }} replace />;
  }
  // Every other API call already 403s with PASSWORD_CHANGE_REQUIRED until
  // this clears (JwtAuthenticationFilter) - redirect here instead of letting
  // every protected page independently discover that the hard way.
  if (currentUser?.mustChangePassword && location.pathname !== ROUTES.changePasswordRequired) {
    return <Navigate to={ROUTES.changePasswordRequired} replace />;
  }
  return <>{children}</>;
}

/**
 * RequireGuest - the inverse guard for login/register: an already-logged-in
 * user shouldn't see the login form again.
 *
 * useLogin()'s hook-level onSuccess sets authStore's isAuthenticated
 * synchronously, before LoginPage's own onSubmit-level onSuccess (the one
 * that knows about `from` and would navigate there) gets a turn to run -
 * React Query calls hook-level callbacks first. Since this guard re-renders
 * reactively off the same isAuthenticated flip, it wins that race and
 * redirects LoginPage away before its own navigate(resolvePostLoginRoute(...))
 * call ever fires, silently dropping `from` and landing on the generic
 * post-login default instead. Reading the same `from` here (set by
 * RegisterPage/LoginPage/HomePage/PublicLayout's redirect-intent state) and
 * resolving it the same way keeps the destination correct - and equally
 * safe from bouncing a freshly-authenticated user straight into
 * /unauthorized (see resolvePostLoginRoute) - regardless of which
 * navigation wins.
 */
export function RequireGuest({ children }: PropsWithChildren) {
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const currentUser = useAuthStore((s) => s.currentUser);
  if (isAuthenticated) {
    const from = (location.state as { from?: To } | null)?.from;
    return <Navigate to={resolvePostLoginRoute(from, currentUser?.roles ?? [])} replace />;
  }
  return <>{children}</>;
}

/**
 * RequireRole - Section 6.1's role guard, e.g. `<RequireRole roles={['PARTNER']}>`.
 *
 * Checks the user's fine-grained ROLE names (decoded from the JWT's `roles`
 * claim - see `@/lib/jwt`), not `accountType`. `accountType` is only the
 * coarse CLIENT/DRIVER/PARTNER/STAFF/CORPORATE enum - it can't distinguish a
 * SUPER_ADMIN from a SUPPORT_AGENT (both are `STAFF`), and doesn't match
 * `CORPORATE_ADMIN` at all (a corporate account's `accountType` is
 * `CORPORATE`, a different string). Checking `roles` instead is what makes
 * every staff-role and the corporate-admin route guard actually work.
 */
export function RequireRole({ roles, children }: PropsWithChildren<{ roles: string[] }>) {
  const currentUser = useAuthStore((s) => s.currentUser);
  if (!currentUser) {
    return <Navigate to={ROUTES.login} replace />;
  }
  if (!roles.some((role) => currentUser.roles.includes(role))) {
    // Tell /unauthorized which account type the page needs, so it can say so instead of a bare "denied".
    return <Navigate to={ROUTES.unauthorized} state={{ requiredRoles: roles }} replace />;
  }
  return <>{children}</>;
}

/**
 * RequirePermission - the permission-based route guard (RBAC->PBAC migration), same shape as
 * RequireRole but checking effective permissions instead of role names. Prefer this for new/migrated
 * admin routes: `<RequirePermission permission="TRIP_VIEW">` or
 * `<RequirePermission anyOf={['TRIP_VIEW', 'TRIP_CREATE']}>`. SUPER_ADMIN always passes (see
 * usePermissions()). Redirects to /unauthorized without ever rendering the protected page's content -
 * frontend hiding is UX only, but "don't even mount the component" avoids a flash of protected UI
 * before the backend's own 403 would come back.
 */
export function RequirePermission({
  permission,
  anyOf,
  children,
}: PropsWithChildren<{ permission?: string; anyOf?: string[] }>) {
  const currentUser = useAuthStore((s) => s.currentUser);
  const { hasPermission, hasAnyPermission } = usePermissions();
  if (!currentUser) {
    return <Navigate to={ROUTES.login} replace />;
  }
  const allowed = permission ? hasPermission(permission) : anyOf ? hasAnyPermission(anyOf) : false;
  if (!allowed) {
    return <Navigate to={ROUTES.unauthorized} replace />;
  }
  return <>{children}</>;
}
